import type { ReactNode } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';

export type Bucket = 'ally' | 'enemy' | 'ban';

/** What a dragged item carries. `from` is 'grid' when dragged out of the catalog. */
export interface DragData {
  championId: string;
  from: Bucket | 'grid';
}

/** A drop onto a champion slot. `index` is the slot position (null = append). */
export interface SlotDropData {
  bucket: Bucket;
  index: number | null;
}

/** A drop onto the "remove" zone (the champion grid): drags off the board. */
export interface RemoveDropData {
  remove: true;
}

export type DropData = SlotDropData | RemoveDropData;

interface DraggableProps {
  id: string;
  data: DragData;
  disabled?: boolean;
  children: ReactNode;
}

/** Wraps children so they can be picked up. Movement past the sensor's distance
 * threshold starts a drag; a plain click still passes through to onClick. */
export function Draggable({ id, data, disabled, children }: DraggableProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data, disabled });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1, touchAction: 'none', cursor: disabled ? 'default' : 'grab' }}
    >
      {children}
    </div>
  );
}

interface DroppableProps {
  id: string;
  data: DropData;
  children: ReactNode;
  className?: string;
}

/** A drop target. Adds `is-over` to the className while a draggable hovers it. */
export function Droppable({ id, data, children, className }: DroppableProps) {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  return (
    <div ref={setNodeRef} className={`${className ?? ''}${isOver ? ' is-over' : ''}`}>
      {children}
    </div>
  );
}
